import React, { useState, createContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  PAGE_SCOPE,
  ORDER_SCOPE,
  ITEM_SCOPE,
  ITEM_GROUP_SCOPE,
} from '../../constants/pageModScopes';

const EMPTY_ARRAY_SINGLETON = [];

/**
 * This context provides a wrapper that manages the page mods, which must be injected at different levels, for the application.
 * 
 * Page mods that are specified via the site configs should be passed in to the provider as props.
 * The expected structure of a single pageUxMod is:
  {    
    modRef: {
      family,
      id,
    },
    scope,
    enabled,
  }
 * ModWrappers should not pass mods with enabled === false into its ModBoss.
 * For this reason, the context exposes multiple "activeMod" properties for
 * each mod level to pass into a Mod Boss (e.g. activePageLevelPageUxMods)
 * 
 * The application's debug tools provide client's an interface for changing the set of page UX mods
 * in the application. For this reason, we expose the setter function for mods at each level,
 * as well as the ability to toggle a mod's enablement.
 * 
 * Note on performance: For production customers, the set of inbound props to this component will likely
 * not change over the course of their page visit. Changing page UX mods at runtime will really be a 
 * debug tools / developer operation. It could be more performant if we memoized the set of mods on each
 * level, and only update a given level's state if mods within it change. But for the time being, the 
 * existing behavior is fine, even if it causes state updates at every level when a page mod is changed.
 */
const PageModsContext = createContext(null);

const PageModsProvider = props => {
  const { children, siteConfigPageUxMods = EMPTY_ARRAY_SINGLETON } = props;

  // Page Level Mods
  const [pageLevelPageUxMods, setPageLevelPageUxMods] = useState(
    siteConfigPageUxMods.filter(mod => mod.scope === PAGE_SCOPE)
  );
  const activePageLevelPageUxMods = pageLevelPageUxMods.filter(
    mod => mod.enabled
  );

  // Order Level Mods
  const [orderLevelPageUxMods, setOrderLevelPageUxMods] = useState(
    siteConfigPageUxMods.filter(mod => mod.scope === ORDER_SCOPE)
  );
  const activeOrderLevelPageUxMods = orderLevelPageUxMods.filter(
    mod => mod.enabled
  );

  // Item Level Mods
  const [itemLevelPageUxMods, setItemLevelPageUxMods] = useState(
    siteConfigPageUxMods.filter(mod => mod.scope === ITEM_SCOPE)
  );
  const activeItemLevelPageUxMods = itemLevelPageUxMods.filter(
    mod => mod.enabled
  );

  // Item Group Level Mods
  const [itemGroupLevelPageUxMods, setItemGroupLevelPageUxMods] = useState(
    siteConfigPageUxMods.filter(mod => mod.scope === ITEM_GROUP_SCOPE)
  );
  const activeItemGroupLevelPageUxMods = itemGroupLevelPageUxMods.filter(
    mod => mod.enabled
  );

  useEffect(() => {
    setPageLevelPageUxMods(
      siteConfigPageUxMods.filter(mod => mod.scope === PAGE_SCOPE)
    );
    setOrderLevelPageUxMods(
      siteConfigPageUxMods.filter(mod => mod.scope === ORDER_SCOPE)
    );
    setItemLevelPageUxMods(
      siteConfigPageUxMods.filter(mod => mod.scope === ITEM_SCOPE)
    );
    setItemGroupLevelPageUxMods(
      siteConfigPageUxMods.filter(mod => mod.scope === ITEM_GROUP_SCOPE)
    );
  }, [
    siteConfigPageUxMods,
    setPageLevelPageUxMods,
    setOrderLevelPageUxMods,
    setItemLevelPageUxMods,
    setItemGroupLevelPageUxMods,
  ]);

  const pageModsContextValues = useMemo(
    () => ({
      pageLevelPageUxMods,
      setPageLevelPageUxMods,
      activePageLevelPageUxMods,
      orderLevelPageUxMods,
      setOrderLevelPageUxMods,
      activeOrderLevelPageUxMods,
      itemLevelPageUxMods,
      setItemLevelPageUxMods,
      activeItemLevelPageUxMods,
      itemGroupLevelPageUxMods,
      setItemGroupLevelPageUxMods,
      activeItemGroupLevelPageUxMods,
    }),
    [
      activeItemGroupLevelPageUxMods,
      activeItemLevelPageUxMods,
      activeOrderLevelPageUxMods,
      activePageLevelPageUxMods,
      itemGroupLevelPageUxMods,
      itemLevelPageUxMods,
      orderLevelPageUxMods,
      pageLevelPageUxMods,
    ]
  );

  return (
    <PageModsContext.Provider value={pageModsContextValues}>
      {children}
    </PageModsContext.Provider>
  );
};

PageModsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  siteConfigPageUxMods: PropTypes.array,
};

export { PageModsContext, PageModsProvider };
